import React from 'react'
import { SelectedArticles } from '../../types/Picklist'
import { Store } from '../../contexts/store-context'
import Tag from '../Tag/Tag'
import { getSingleSiteMaxQuantity, MISSING_STORE_NAME, SOLD_SINCE_LAST_RESTOCK, STOCK_STATUS_OUT } from './picklistHelpers'
import ItemImage from '../ItemImage/ItemImage'

export interface IPrintPicklistProps {
  selectedArticlesForPrint: SelectedArticles
  pickDisplay: string
  articlesBySite: boolean
  articlesByCategory: boolean
  sites: Store[]
  showSingleSiteMaxQuantity: boolean
}

const renderStockInfo = (displayBySold: boolean, stockCount: number, restockedCount: number, soldCount: number, maxQuantityCount: number | null | undefined) => {
  if (displayBySold) {
    return <td className='pr-4 font-semibold text-right'>{`${soldCount ?? 0} st`}</td>
  }
  const hasMaxQuantity = maxQuantityCount != null
  return <td className='pr-4 font-semibold text-right'>{`${stockCount ?? 0}/${hasMaxQuantity ? maxQuantityCount : restockedCount ?? 0}${hasMaxQuantity ? '*' : ''}`}</td>
}

const renderStatusTag = (status: string | undefined) => {
  if (!status) return null
  return <Tag text={status} type={status === STOCK_STATUS_OUT ? 'primary' : 'secondary'} />
}

const ArticleCard= ({ ean, name }: { ean: string, name: string }) => (
  <>
    <ItemImage ean={ean} />
    <div className=" pl-5 pb-3 flex flex-col">
      <span>{name}</span>
      <span className="text-[13px] text-[var(--color-grey-mid-light)]">{ean}</span>
    </div>
  </>
)

const RenderArticles = ({ articles, displayBySold, articlesBySite, articlesByCategory, sites, showSingleSiteMaxQuantity }: { articles: SelectedArticles, displayBySold: boolean, articlesBySite: boolean, articlesByCategory: boolean, sites: Store[], showSingleSiteMaxQuantity: boolean }) => {
  return (
    <tbody>
      {/* No categories selected */}
      {Array.isArray(articles) ? (
        articles.map((article) => (
          <React.Fragment key={article.ean}>
            <tr className={`no-break ${articlesBySite ? 'bg-gray-100 article-group-start' : ''}`}>
              <td className={`px-4 py-2 ${articlesBySite ? 'font-bold' : ''}`}>
                <div className="flex items-center gap-1">
                  <ArticleCard ean={article.ean} name={article.name} />
                </div>
              </td>
              <td className="px-2 py-2">
                {!articlesBySite && article.stockStatus && renderStatusTag(article.stockStatus)}
              </td>
              {renderStockInfo(
                displayBySold, 
                article.totalInStock ?? 0, 
                article.totalRestocked ?? 0, 
                article.totalSold ?? 0, 
                getSingleSiteMaxQuantity(article.storeAmounts, showSingleSiteMaxQuantity, articlesBySite))}
            </tr>
            {articlesBySite &&
                article.storeAmounts?.map((store, idx) => {
                  const siteName = sites.find((s) => s.id === store.storeId)?.name ?? MISSING_STORE_NAME
                  return (
                    <tr key={`${article.ean}-store-${idx}`} className="no-break site-divider">
                      <td className="pl-6 pr-4 py-2">
                        {siteName}
                      </td>
                      <td className="px-2 py-2">
                        {store.status && renderStatusTag(store.status)}
                      </td>
                      {renderStockInfo(
                        displayBySold,
                        store.quantityInStockForStore ?? 0,
                        store.restockedQuantityForStore ?? 0,
                        store.soldForStore ?? 0,
                        store.maxQuantityForStore ?? undefined
                      )}
                    </tr>
                  )
                })}
          </React.Fragment>
        ))
      ) : (
        /* Categories selected */
        Object.entries(articles).map(([category, articlesInCategory], index) => (
          <React.Fragment key={`cat-${category}-${index}`}>
            {articlesByCategory && (
              <tr className="no-break">
                <td colSpan={3} className="px-4 pt-4 pb-1 font-bold text-xl" style={{ fontFamily: 'ICARubrik' }}>
                  {category}
                </td>
              </tr>
            )}
            {articlesInCategory.map((article) => (
              <React.Fragment key={article.ean}>
                <tr className={`no-break ${articlesBySite ? 'bg-gray-100 article-group-start' : ''}`}>
                  <td className={`px-4 py-2 ${articlesBySite ? 'font-bold' : ''}`}>
                    <div className="flex items-center gap-1"> 
                      <ArticleCard ean={article.ean} name={article.name} />
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    {!articlesBySite && article.stockStatus && renderStatusTag(article.stockStatus)}
                  </td>
                  {renderStockInfo(
                    displayBySold,
                    article.totalInStock ?? 0,
                    article.totalRestocked ?? 0,
                    article.totalSold ?? 0,
                    getSingleSiteMaxQuantity(article.storeAmounts, showSingleSiteMaxQuantity, articlesBySite))}
                </tr>
                {articlesBySite &&
                    article.storeAmounts?.map((store, idx) => {
                      const siteName = sites.find((s) => s.id === store.storeId)?.name ?? MISSING_STORE_NAME
                      return (
                        <tr key={`${article.ean}-store-${idx}`} className={`no-break ${idx === (article.storeAmounts?.length ?? 0) - 1 ? 'site-group-end' : ''}`}>
                          <td className="pl-6 pr-4 py-2">
                            {siteName}
                          </td>
                          <td className="px-2 py-2">
                            {store.status && renderStatusTag(store.status)}
                          </td>
                          {renderStockInfo(displayBySold, store.quantityInStockForStore ?? 0, store.restockedQuantityForStore ?? 0, store.soldForStore ?? 0, store.maxQuantityForStore ?? undefined)}
                        </tr>
                      )
                    })}
              </React.Fragment>
            ))}
          </React.Fragment>
        ))
      )}
    </tbody>
  )
}

export default function PrintPicklist({ selectedArticlesForPrint, pickDisplay, articlesBySite, articlesByCategory, sites, showSingleSiteMaxQuantity }: IPrintPicklistProps) {
  const displayBySold = pickDisplay === SOLD_SINCE_LAST_RESTOCK
  return (
    <>
      <h1 className="text-4xl/10 font-bold pt-4 pb-2 px-4" style={{ fontFamily: 'ICARubrik' }}>
        Plocklista
      </h1>
      <table className="w-full">
        <thead>
          <tr className="bg-[#EDEDED] uppercase text-xs/[14px] font-bold">
            <th className="text-left px-3 py-2">Artikel</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-right px-3 py-2 whitespace-nowrap">{displayBySold ? 'Att plocka' : 'I skåp / Påfyllt (Eller max*)'}</th>
          </tr>
        </thead>
        <RenderArticles
          articles={selectedArticlesForPrint}
          displayBySold={displayBySold}
          articlesBySite={articlesBySite}
          articlesByCategory={articlesByCategory}
          sites={sites}
          showSingleSiteMaxQuantity={showSingleSiteMaxQuantity}
        />
      </table>
    </>
  )
}
